import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { TrendingUp, TrendingDown, AlertTriangle, CalendarIcon } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from "recharts";
import { format } from "date-fns";
import { useState, useMemo } from "react";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import Exposures from "./Exposures";
import Fees from "./Fees";

interface PerformanceAttribution {
  category: string;
  name: string;
  returnDollar: number;
  returnPercent: number;
  contribution: number;
  avgWeight: number;
  startValue: number;
  endValue: number;
}

// Helper to classify holdings into asset classes
const classifyAssetClass = (holding: { name?: string; symbol?: string }): string => {
  const name = (holding.name || '').toLowerCase();
  const symbol = (holding.symbol || '').toUpperCase();

  if (symbol === 'USD' || symbol === 'SGD' || name.includes('cash') || name.includes('money market')) {
    return 'Cash & Equivalents';
  }
  if (name.includes('buyout') || name.includes('private equity') || name.includes('pe fund')) {
    return 'Private Equity';
  }
  if (name.includes('venture') || name.includes('vc fund')) {
    return 'Venture Capital';
  }
  if (name.includes('bond') || name.includes('treasury') || name.includes('fixed income')) {
    return 'Fixed Income';
  }
  if (name.includes('reit') || name.includes('real estate') || name.includes('infrastructure')) {
    return 'Real Assets';
  }
  return 'Public Equity';
};

// Helper to classify geography from holding name
const classifyGeography = (holding: { name?: string }): string => {
  const name = (holding.name || '').toLowerCase();
  if (name.includes('asia') || name.includes('apac') || name.includes('singapore') || name.includes('japan') || name.includes('china')) {
    return 'Asia Pacific';
  }
  if (name.includes('europe') || name.includes('uk') || name.includes('euro')) {
    return 'Europe';
  }
  return 'North America';
};

// Helper to classify strategy
const classifyStrategy = (holding: { name?: string }): string => {
  const name = (holding.name || '').toLowerCase();
  if (name.includes('growth') || name.includes('venture')) {
    return 'Growth';
  }
  if (name.includes('value') || name.includes('dividend')) {
    return 'Value';
  }
  if (name.includes('income') || name.includes('bond') || name.includes('fixed')) {
    return 'Income';
  }
  return 'Opportunistic';
};

const CHART_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
];

type PeriodPreset = "ytd" | "qtd" | "last-quarter" | "last-year" | "custom";

export default function Analytics() {
  const { orgId, familyId } = useTenant();
  const [periodPreset, setPeriodPreset] = useState<PeriodPreset>("ytd");
  const [startDate, setStartDate] = useState<Date>(() => {
    const date = new Date();
    date.setMonth(0, 1); // January 1st of current year
    return date;
  });
  const [endDate, setEndDate] = useState<Date>(new Date());

  // Fetch current holdings
  const { data: holdingsData } = useQuery({
    queryKey: ['analytics-holdings', orgId, familyId],
    queryFn: async () => {
      if (!orgId) return [];

      let query = supabase
        .from('holdings')
        .select('*')
        .eq('org_id', orgId);

      if (familyId) {
        query = query.eq('family_id', familyId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: !!orgId
  });

  // Fetch position metrics for period comparison
  const { data: metricsData } = useQuery({
    queryKey: ['analytics-metrics', orgId, familyId, startDate, endDate],
    queryFn: async () => {
      if (!orgId) return { start: [], end: [] };

      const startDateStr = format(startDate, 'yyyy-MM-dd');
      const endDateStr = format(endDate, 'yyyy-MM-dd');

      // Get metrics near start date
      let startQuery = supabase
        .from('position_metrics')
        .select('*')
        .eq('org_id', orgId)
        .lte('period_end', startDateStr)
        .order('period_end', { ascending: false });

      if (familyId) {
        startQuery = startQuery.eq('family_id', familyId);
      }

      // Get metrics near end date
      let endQuery = supabase
        .from('position_metrics')
        .select('*')
        .eq('org_id', orgId)
        .lte('period_end', endDateStr)
        .order('period_end', { ascending: false });

      if (familyId) {
        endQuery = endQuery.eq('family_id', familyId);
      }

      const [startResult, endResult] = await Promise.all([
        startQuery.limit(50),
        endQuery.limit(50)
      ]);

      return {
        start: startResult.data || [],
        end: endResult.data || []
      };
    },
    enabled: !!orgId
  });

  // Calculate attribution data from holdings
  const { assetClassAttribution, geographyAttribution, strategyAttribution, managerAttribution } = useMemo(() => {
    if (!holdingsData || holdingsData.length === 0) {
      return {
        assetClassAttribution: [] as PerformanceAttribution[],
        geographyAttribution: [] as PerformanceAttribution[],
        strategyAttribution: [] as PerformanceAttribution[],
        managerAttribution: [] as PerformanceAttribution[]
      };
    }

    const totalMV = holdingsData.reduce((sum, h) => sum + (Number(h.mv) || 0), 0);
    const totalCost = holdingsData.reduce((sum, h) => sum + (Number(h.cost) || 0), 0);

    // Helper to calculate attribution for a grouping
    const calculateAttribution = (groupBy: (h: { name?: string; symbol?: string; mv?: number; cost?: number }) => string, category: string): PerformanceAttribution[] => {
      const grouped: Record<string, { mv: number; cost: number }> = {};

      holdingsData.forEach(h => {
        const key = groupBy(h);
        if (!grouped[key]) {
          grouped[key] = { mv: 0, cost: 0 };
        }
        grouped[key].mv += Number(h.mv) || 0;
        grouped[key].cost += Number(h.cost) || 0;
      });

      return Object.entries(grouped).map(([name, data]) => {
        const returnDollar = data.mv - data.cost;
        const returnPercent = data.cost > 0 ? (returnDollar / data.cost) * 100 : 0;
        const avgWeight = totalMV > 0 ? (data.mv / totalMV) * 100 : 0;
        const contribution = totalCost > 0 ? (returnDollar / totalCost) * 100 : 0;

        return {
          category,
          name,
          returnDollar,
          returnPercent,
          contribution,
          avgWeight,
          startValue: data.cost,
          endValue: data.mv
        };
      }).sort((a, b) => b.endValue - a.endValue);
    };

    return {
      assetClassAttribution: calculateAttribution(classifyAssetClass, 'Asset Class'),
      geographyAttribution: calculateAttribution(classifyGeography, 'Geography'),
      strategyAttribution: calculateAttribution(classifyStrategy, 'Strategy'),
      managerAttribution: calculateAttribution(
        (h) => {
          // Extract manager from name (simplified)
          const name = h.name || h.symbol || 'Direct Holdings';
          const words = name.split(' ');
          return words.length > 2 ? words.slice(0, 2).join(' ') : name;
        },
        'Manager'
      ).slice(0, 5) // Top 5 managers
    };
  }, [holdingsData]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  const handlePeriodPresetChange = (preset: PeriodPreset) => {
    setPeriodPreset(preset);
    const today = new Date();

    switch (preset) {
      case "ytd":
        setStartDate(new Date(today.getFullYear(), 0, 1));
        setEndDate(today);
        break;
      case "qtd": {
        const currentQuarter = Math.floor(today.getMonth() / 3);
        setStartDate(new Date(today.getFullYear(), currentQuarter * 3, 1));
        setEndDate(today);
        break;
      }
      case "last-quarter": {
        const lastQuarter = Math.floor(today.getMonth() / 3) - 1;
        const lastQuarterYear = lastQuarter < 0 ? today.getFullYear() - 1 : today.getFullYear();
        const adjustedQuarter = lastQuarter < 0 ? 3 : lastQuarter;
        setStartDate(new Date(lastQuarterYear, adjustedQuarter * 3, 1));
        setEndDate(new Date(lastQuarterYear, adjustedQuarter * 3 + 3, 0));
        break;
      }
      case "last-year":
        setStartDate(new Date(today.getFullYear() - 1, 0, 1));
        setEndDate(new Date(today.getFullYear() - 1, 11, 31));
        break;
      case "custom":
        // Keep current dates for custom
        break;
    }
  };

  const totalReturn = assetClassAttribution.reduce((sum, item) => sum + item.returnDollar, 0);
  const totalStartValue = assetClassAttribution.reduce((sum, item) => sum + item.startValue, 0);
  const totalEndValue = assetClassAttribution.reduce((sum, item) => sum + item.endValue, 0);
  const totalReturnPercent = totalStartValue > 0 ? ((totalEndValue - totalStartValue) / totalStartValue) * 100 : 0;

  const AttributionChart = ({ title, data }: { title: string; data: PerformanceAttribution[] }) => {
    const chartData = data.map(item => ({
      name: item.name,
      returnDollar: item.returnDollar,
      returnPercent: item.returnPercent,
      contribution: item.contribution,
    }));

    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45} 
                    textAnchor="end" 
                    height={100}
                    className="text-xs"
                  />
                  <YAxis className="text-xs" />
                  <Tooltip
                    formatter={(value: number, name: string) => {
                      if (name === "returnDollar") return formatCurrency(value);
                      if (name === "returnPercent" || name === "contribution") return formatPercent(value);
                      return value;
                    }}
                  />
                  <Bar dataKey="returnPercent" fill="hsl(var(--chart-1))" name="Return %" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            
            <div className="space-y-2 flex flex-col justify-center">
              {data.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full flex-shrink-0" 
                      style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
                    />
                    <span className="font-medium text-sm">{item.name}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge variant={item.returnPercent >= 0 ? "default" : "destructive"}>
                      {formatPercent(item.returnPercent)}
                    </Badge>
                    <span className="font-mono text-xs text-muted-foreground">
                      {formatCurrency(item.returnDollar)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const PerformanceAttributionView = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Performance Attribution</h2>
          <p className="text-muted-foreground">
            Return breakdown by asset class, geography, manager, and strategy
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => handlePeriodPresetChange("ytd")} className={periodPreset === "ytd" ? "bg-accent" : ""}>
            YTD
          </Button>
          <Button variant="outline" size="sm" onClick={() => handlePeriodPresetChange("qtd")} className={periodPreset === "qtd" ? "bg-accent" : ""}>
            QTD
          </Button>
          <Button variant="outline" size="sm" onClick={() => handlePeriodPresetChange("last-quarter")} className={periodPreset === "last-quarter" ? "bg-accent" : ""}>
            Last Quarter
          </Button>
          <Button variant="outline" size="sm" onClick={() => handlePeriodPresetChange("last-year")} className={periodPreset === "last-year" ? "bg-accent" : ""}>
            Last Year
          </Button>
          
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "justify-start text-left font-normal",
                  periodPreset === "custom" && "bg-accent"
                )}
              >
                <CalendarIcon className="h-4 w-4" />
                {periodPreset === "custom" ? (
                  `${format(startDate, "MMM d")} - ${format(endDate, "MMM d, yyyy")}`
                ) : (
                  "Custom"
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <div className="p-4 space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Start Date</label>
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={(date) => {
                      if (date) {
                        setStartDate(date);
                        setPeriodPreset("custom");
                      }
                    }}
                    initialFocus
                    className={cn("pointer-events-auto")}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">End Date</label>
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={(date) => {
                      if (date) {
                        setEndDate(date);
                        setPeriodPreset("custom");
                      }
                    }}
                    disabled={(date) => date < startDate}
                    className={cn("pointer-events-auto")}
                  />
                </div>
              </div>
            </PopoverContent>
          </Popover>
          
        </div>
      </div>
      
      <Card className="bg-muted/50">
        <CardContent className="pt-4">
          <p className="text-sm text-muted-foreground">
            Showing performance attribution for period: <span className="font-semibold text-foreground">{format(startDate, "MMM d, yyyy")}</span> to <span className="font-semibold text-foreground">{format(endDate, "MMM d, yyyy")}</span>
          </p>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Return</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">{formatCurrency(totalReturn)}</div>
            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
              <TrendingUp className="h-3 w-3" />
              {formatPercent(totalReturnPercent)} for the period
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Starting Value</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalStartValue)}</div>
            <p className="text-xs text-muted-foreground">Beginning of period</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Ending Value</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalEndValue)}</div>
            <p className="text-xs text-muted-foreground">End of period</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Best Performer</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {strategyAttribution.length > 0 ? strategyAttribution.reduce((max, item) => item.returnPercent > max.returnPercent ? item : max).name : 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">
              {strategyAttribution.length > 0 ? formatPercent(strategyAttribution.reduce((max, item) => item.returnPercent > max.returnPercent ? item : max).returnPercent) : '0.00%'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Attribution Charts */}
      <div className="grid gap-4 md:grid-cols-2">
        <AttributionChart title="Asset Class Attribution" data={assetClassAttribution} />
        <AttributionChart title="Geography Attribution" data={geographyAttribution} />
        <AttributionChart title="Manager Attribution" data={managerAttribution} />
        <AttributionChart title="Strategy Attribution" data={strategyAttribution} />
      </div>

      {/* Detailed Table */}
      <Card>
        <CardHeader>
          <CardTitle>Detailed Performance Attribution</CardTitle>
          <CardDescription>
            Comprehensive breakdown of returns and contributions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Start Value</TableHead>
                <TableHead className="text-right">End Value</TableHead>
                <TableHead className="text-right">Return ($)</TableHead>
                <TableHead className="text-right">Return (%)</TableHead>
                <TableHead className="text-right">Contribution</TableHead>
                <TableHead className="text-right">Avg Weight</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assetClassAttribution.map((item, idx) => (
                <TableRow key={idx}>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell className="text-right font-mono">{formatCurrency(item.startValue)}</TableCell>
                  <TableCell className="text-right font-mono">{formatCurrency(item.endValue)}</TableCell>
                  <TableCell className="text-right font-semibold">
                    {item.returnDollar >= 0 ? (
                      <span className="text-success">{formatCurrency(item.returnDollar)}</span>
                    ) : (
                      <span className="text-destructive">{formatCurrency(item.returnDollar)}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant={item.returnPercent >= 0 ? "default" : "destructive"}>
                      {item.returnPercent >= 0 ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
                      {formatPercent(item.returnPercent)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono">{formatPercent(item.contribution)}</TableCell>
                  <TableCell className="text-right font-mono">{formatPercent(item.avgWeight)}</TableCell>
                </TableRow>
              ))}
              <TableRow className="border-t-2 bg-primary/5 font-bold">
                <TableCell>Total</TableCell>
                <TableCell className="text-right">{formatCurrency(totalStartValue)}</TableCell>
                <TableCell className="text-right">{formatCurrency(totalEndValue)}</TableCell>
                <TableCell className="text-right text-success">{formatCurrency(totalReturn)}</TableCell>
                <TableCell className="text-right">
                  <Badge variant="default" className="gap-1">
                    <TrendingUp className="h-3 w-3" />
                    {formatPercent(totalReturnPercent)}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">{formatPercent(assetClassAttribution.reduce((sum, item) => sum + item.contribution, 0))}</TableCell>
                <TableCell className="text-right">100.00%</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="flex-1 space-y-4 px-6 py-6">
      
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Analytics</h1>
        <p className="text-muted-foreground">
          Portfolio analytics including exposures, fees, and performance attribution
        </p>
      </div>

      <Tabs defaultValue="performance" className="space-y-4">
        <TabsList>
          <TabsTrigger value="performance">Performance Attribution</TabsTrigger>
          <TabsTrigger value="exposures">Exposures</TabsTrigger>
          <TabsTrigger value="fees">Fees</TabsTrigger>
        </TabsList>
        
        <TabsContent value="performance" className="space-y-4">
          <PerformanceAttributionView />
        </TabsContent>
        
        <TabsContent value="exposures">
          <div className="space-y-4">
            <Exposures />
          </div>
        </TabsContent>
        
        <TabsContent value="fees">
          <div className="space-y-4">
            <Fees />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
